import {
  useCreateAccountFlagDefinition,
  useRefreshAccountFlagDefinitions,
  useUpdateAccountFlagDefinition,
} from '@app/sdk';
import { FormContainer, Input, InputProps, Select, SelectProps, toSelectOption } from '@app/shared';
import { ComponentErrorBoundary } from '@app/utilities';
import { FormProvider, useForm } from 'react-hook-form';

type AccountFlagDefinitionFormModel = {
  id: string;
  key: string;
  description: string;
  usage: string;
};

export function AccountFlagDefinitionForm({
  accountFlagDefinition,
  onUpdate,
}: {
  accountFlagDefinition: AccountFlagDefinitionFormModel;
  onUpdate: () => Promise<void>;
}) {
  const { mutateAsync: create, isPending: isCreating } = useCreateAccountFlagDefinition();
  const { mutateAsync: update, isPending: isUpdating } = useUpdateAccountFlagDefinition();
  const refreshAccountFlagDefinitions = useRefreshAccountFlagDefinitions();

  async function onSaveAccountFlagDefinition(data: AccountFlagDefinitionFormModel) {
    if (accountFlagDefinition?.id) {
      const request = {
        key: data.key,
        description: data.description,
        usage: data.usage,
      };
      await update({ id: accountFlagDefinition.id, request });
    } else {
      const request = {
        accountFlagDefinitions: [
          {
            key: data.key,
            description: data.description,
            usage: data.usage,
          },
        ],
      };
      await create(request);
    }
    await refreshAccountFlagDefinitions();
    await onUpdate();
  }

  const formContext = useForm<AccountFlagDefinitionFormModel>();

  const keyInput: InputProps<AccountFlagDefinitionFormModel> = {
    name: 'key',
    label: 'Key',
    options: { required: 'Key is required' },
    defaultValue: accountFlagDefinition?.key,
  };

  const descriptionInput: InputProps<AccountFlagDefinitionFormModel> = {
    name: 'description',
    label: 'Description',
    options: { required: 'Description is required' },
    defaultValue: accountFlagDefinition?.description,
  };

  const usageInput: SelectProps<AccountFlagDefinitionFormModel> = {
    name: 'usage',
    label: 'Usage',
    selectOptions: toSelectOption(['Treatment', 'Manual', 'All']),
    options: { required: 'Usage is required' },
    defaultValue: accountFlagDefinition?.usage,
  };

  return (
    <ComponentErrorBoundary>
      <FormProvider {...formContext}>
        <FormContainer isSubmitting={isCreating || isUpdating} onSubmit={onSaveAccountFlagDefinition} disableButtons>
          <Input {...keyInput} disabled />
          <Input {...descriptionInput} disabled />
          <Select {...usageInput} disabled />
        </FormContainer>
      </FormProvider>
    </ComponentErrorBoundary>
  );
}
